/** @jsxImportSource @emotion/react */
import { Form, Formik, FormikProps } from 'formik';
import React, { useRef, useState } from 'react';

import {
    StoryblokModule,
    StoryblokFlexibleComponentForm,
    StoryblokFormFieldItem,
} from '../../storyblok';

import { FormFields } from './FormFields';
import { useFormOnSubmitEvent } from './useFormSubmitEvent';
import { FormContext } from './context';
import styled from '@emotion/styled';
import { Loader } from '../../components/Loader';

export type FormButtonThemeVariant = 'roundedBahamaBlue';
export type InputThemeVariant = 'ghostRounded';

/**
 * Initial values with correct field names (keys) are needed to use Formik
 * properly. Without initial values it won't be possible to use
 * formikHelper.resetForm()
 */
const parseFormInitialValues = (formFields: StoryblokFormFieldItem[]) => {
    return formFields.reduce<Record<string, unknown>>((obj, item) => {
        /**
         * Reduce only Form Field components, because these will be part
         * of the actual form and will be used to represent initial form
         * data.
         */
        if (item.component !== 'FormField') return obj;
        /**
         * It is possible to set field's initial value in Storyblok field.
         * We need to set correct default value
         * - boolean, if initialValue is 'true' | 'false'
         * - string in other cases
         */
        if (item.initialValue)
            return { ...obj, [item['name']]: item.initialValue };

        return {
            ...obj,
            [item['name']]: '',
        };
    }, {});
};

export const Formi: React.VFC<
    StoryblokModule<StoryblokFlexibleComponentForm>
> = ({
    items,
    formSubmitEvent,
    successMessage,
    buttonThemeVariant,
    inputThemeVariant,
    ...restProps
}) => {
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const { onSubmit, submissionState, setSubmissionState } =
        useFormOnSubmitEvent(formSubmitEvent);
    const formRef = useRef<FormikProps<Record<string, unknown>>>(null);

    return (
        <FormContext.Provider
            value={{
                buttonThemeVariant,
                inputThemeVariant,
                formRef,
                items,
                initialValues: parseFormInitialValues(items),
                // setOnSubmit,
            }}
        >
            <Formik
                initialValues={parseFormInitialValues(items)}
                onSubmit={(values, formikHelpers) => {
                    setSubmissionState({ state: 'loading' });
                    onSubmit({ values, formikHelpers });
                }}
                innerRef={formRef}
            >
                <>
                    {submissionState.state === 'loading' && <Loader />}
                    {submissionState.state === 'initial' && (
                        <Form {...restProps}>
                            <FormFields items={items} />
                        </Form>
                    )}
                    {submissionState.state === 'submitted' && (
                        <>
                            {submissionState.submissionScreen === 'message' ? (
                                <>
                                    <SuccessMessage>
                                        {successMessage}
                                    </SuccessMessage>
                                    {submissionState.renderForm && (
                                        <Form {...restProps}>
                                            <FormFields items={items} />
                                        </Form>
                                    )}
                                </>
                            ) : (
                                <div>{submissionState.submissionScreen}</div>
                            )}
                        </>
                    )}
                </>
            </Formik>
        </FormContext.Provider>
    );
};

const SuccessMessage = styled.div`
    border: 2px solid #b9deb7;
    padding: 15px;
    margin-bottom: 15px;
    text-align: center;
`;
